import { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { RegraDeParaIBSCBSApi, RegraIbsCbsEntradaApi } from '@/services/Configuracoes';
import {
  CODIGOS_DE_CREDITO,
  FORMULARIO_VAZIO,
  type FormularioClassificacao,
  erroDoFormulario,
  formularioDaLinha,
  paraSalvar,
} from './regras-ibs-cbs';

const ROTULOS_DO_CREDITO: Record<string, string> = {
  S: 'S — com crédito',
  N: 'N — sem crédito',
  P: 'P — presumido',
  X: 'X — ajuste',
  pendente: 'pendente',
};

const CLASSE_DO_SELECT =
  'h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50';

interface Props {
  aberto: boolean;
  /** A linha a alterar; sem ela, o diálogo inclui uma linha nova. */
  linha?: RegraDeParaIBSCBSApi;
  regras: RegraIbsCbsEntradaApi[];
  salvando: boolean;
  onFechar: () => void;
  onSalvar: (linha: Partial<RegraDeParaIBSCBSApi>) => void;
}

/** Inclui ou altera uma classificação de IBS/CBS na entrada (SPEC-002 §2.1, item 4). */
export function ClassificacaoIbsCbsDialog({
  aberto,
  linha,
  regras,
  salvando,
  onFechar,
  onSalvar,
}: Props) {
  const [form, setForm] = useState<FormularioClassificacao>(FORMULARIO_VAZIO);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setForm(linha ? formularioDaLinha(linha) : FORMULARIO_VAZIO);
    setErro(null);
  }, [aberto, linha]);

  const alterar = (campo: keyof FormularioClassificacao) => (valor: string) =>
    setForm(atual => ({ ...atual, [campo]: valor }));

  const salvar = () => {
    const problema = erroDoFormulario(form);
    setErro(problema);
    if (!problema) onSalvar(paraSalvar(form, linha));
  };

  return (
    <Dialog open={aberto} onOpenChange={aberta => !aberta && onFechar()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {linha ? `Alterar a classificação ${linha.ClassTrib}` : 'Incluir classificação'}
          </DialogTitle>
          <DialogDescription>
            {linha
              ? 'A linha passa a ser "alterada pela empresa" e deixa de seguir as versões novas do padrão.'
              : 'A linha nova fica "incluída pela empresa" e vale para todos os contribuintes da empresa.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Campo id="classtrib" rotulo="cClassTrib">
            <Input
              id="classtrib"
              value={form.classTrib}
              disabled={!!linha}
              maxLength={6}
              onChange={e => {
                const valor = e.target.value.replace(/\D/g, '');
                setForm(atual => ({ ...atual, classTrib: valor, cst: valor.slice(0, 3) }));
              }}
            />
          </Campo>
          <Campo id="cst" rotulo="CST">
            <Input id="cst" value={form.cst} disabled maxLength={3} readOnly />
          </Campo>
          <Campo id="regra" rotulo="Regra" className="col-span-2">
            <Input
              id="regra"
              list="regras-ibs-cbs"
              value={form.regra}
              onChange={e => alterar('regra')(e.target.value.toUpperCase())}
            />
            <datalist id="regras-ibs-cbs">
              {regras.map(regra => (
                <option key={regra.Regra} value={regra.Regra}>
                  {regra.NomeRegra}
                </option>
              ))}
            </datalist>
          </Campo>

          <Campo id="descricao" rotulo="Descrição" className="col-span-2 sm:col-span-4">
            <Input
              id="descricao"
              value={form.descricao}
              onChange={e => alterar('descricao')(e.target.value)}
            />
          </Campo>

          <Campo id="vigencia-inicio" rotulo="Vigência: início" className="col-span-2">
            <Input
              id="vigencia-inicio"
              type="date"
              value={form.vigenciaInicio}
              onChange={e => alterar('vigenciaInicio')(e.target.value)}
            />
          </Campo>
          <Campo id="vigencia-fim" rotulo="Vigência: fim (opcional)" className="col-span-2">
            <Input
              id="vigencia-fim"
              type="date"
              value={form.vigenciaFim}
              onChange={e => alterar('vigenciaFim')(e.target.value)}
            />
          </Campo>

          <Campo id="reducao-ibs" rotulo="Redução IBS (%)">
            <Input
              id="reducao-ibs"
              inputMode="decimal"
              value={form.reducaoIbs}
              onChange={e => alterar('reducaoIbs')(e.target.value)}
            />
          </Campo>
          <Campo id="reducao-cbs" rotulo="Redução CBS (%)">
            <Input
              id="reducao-cbs"
              inputMode="decimal"
              value={form.reducaoCbs}
              onChange={e => alterar('reducaoCbs')(e.target.value)}
            />
          </Campo>
          <Campo id="credito-ibs" rotulo="Crédito IBS">
            <select
              id="credito-ibs"
              className={CLASSE_DO_SELECT}
              value={form.creditoIbs}
              onChange={e => alterar('creditoIbs')(e.target.value)}
            >
              {CODIGOS_DE_CREDITO.map(codigo => (
                <option key={codigo} value={codigo}>
                  {ROTULOS_DO_CREDITO[codigo]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo id="credito-cbs" rotulo="Crédito CBS">
            <select
              id="credito-cbs"
              className={CLASSE_DO_SELECT}
              value={form.creditoCbs}
              onChange={e => alterar('creditoCbs')(e.target.value)}
            >
              {CODIGOS_DE_CREDITO.map(codigo => (
                <option key={codigo} value={codigo}>
                  {ROTULOS_DO_CREDITO[codigo]}
                </option>
              ))}
            </select>
          </Campo>

          <Campo
            id="tratamento"
            rotulo="Tratamento na entrada"
            className="col-span-2 sm:col-span-4"
          >
            <Textarea
              id="tratamento"
              rows={2}
              value={form.tratamento}
              onChange={e => alterar('tratamento')(e.target.value)}
            />
          </Campo>
        </div>

        {erro && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{erro}</span>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button onClick={salvar} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Campo({
  id,
  rotulo,
  className,
  children,
}: {
  id: string;
  rotulo: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label htmlFor={id}>{rotulo}</Label>
      {children}
    </div>
  );
}
